import { useEffect } from "react";
import Head from "next/head";
import Link from "next/link";

import useLocalStorage from "hooks/useLocalStorage";
import { getCanonicalSiteUrl } from "@/lib/regions/client";

import Nav from "@/components/Nav";
import Search from "@/components/Search";
import SnapperWidget from "@/components/SnapperWidget";
import Favourites from "@/components/Favourites";

import LocationMarker from "@catchy/assets/svgs/location-mono.svg";

export default function IndexPage({
  isApple,
  isClient,
  mapsSupported,
  preferredStopNames,
}) {
  const siteUrl = getCanonicalSiteUrl();
  const [lastMain, setLastMain] = useLocalStorage("last_main", null);

  useEffect(() => {
    setLastMain("bus");
  }, [setLastMain]);

  const title =
    "Wellington bus, train, ferry and cable car departures — Catchy";
  const description =
    "Check live Wellington bus, train, ferry and cable car departures, nearby stops, and trip maps.";

  return (
    <>
      <Head>
        <title key="title">{title}</title>
        <meta name="twitter:text:title" content={title} key="twitterTitle" />
        <meta
          name="twitter:text:description"
          content={description}
          key="twitterDescription"
        />
        <meta property="og:title" content={title} key="ogTitle" />
        <meta
          property="og:description"
          content={description}
          key="ogDescription"
        />
        <meta name="description" content={description} key="description" />
        <meta
          property="og:image"
          content="https://catchy.nz/share/share-bus.png"
          key="ogImage"
        />
        <meta property="og:url" content={siteUrl} key="ogUrl" />
        <link rel="canonical" href={siteUrl} key="canonicalUrl" />
      </Head>
      <Nav current="bus" />
      <div className="mb-20 flex flex-col pt-4">
        <Search type="bus" preferredStopNames={preferredStopNames} />
        {isClient && mapsSupported ? (
          <div className="px-[20px] py-2">
            <Link
              href="/map?from=bus"
              className="flex w-full items-center justify-center rounded-full py-2 pl-4 pr-6 text-center font-semibold text-blue-600 ring-1 ring-blue-500/50 transition-colors duration-200 hover:bg-blue-400/10 focus:outline-none focus-visible:ring dark:text-blue-400 dark:ring-blue-400/50"
            >
              <LocationMarker className="mr-2 h-[1.125rem] w-[1.125rem]" />
              Map of stops and current trips
            </Link>
          </div>
        ) : (
          ""
        )}
        <div className="mt-4 flex flex-col gap-8">
          <SnapperWidget isApple={isApple} type="bus" isClient={isClient} />
          <Favourites
            isApple={isApple}
            isClient={isClient}
            preferredStopNames={preferredStopNames}
            type="bus"
          />
        </div>
      </div>
    </>
  );
}
